import React, { FC, useId } from 'react';
import styled from 'styled-components';
import { FormControl } from 'src/components/widgets/formControl';
import { AdvancedSelect } from 'src/components/widgets/select';
import Stack from 'src/components/widgets/stack/stack';
import { BaseCardTag } from 'src/components/widgets/tag';
import { ValidationMessageStyled } from 'src/components/widgets/validation/validationMessage';
import { T, stringFormat } from 'src/shared/helpers';
import type { ReplacementCandidate } from './variantMappingRepairCandidates';
import { replacementKey } from './variantMappingRepairStrategies/manual';
import type { AttributeReplacement } from './variantMappingRepairStrategies/types';
import { describeValueReplacementConflict } from './variantMappingRepairMessages';

type ValueChoice = { value: string; label: string; targetOptionID: string; targetValueID: string };

const ChoiceLabel = styled(Stack).attrs({ as: 'span' })`
    align-items: center;
    flex-wrap: wrap;
`;

const ChoiceStatus = styled(BaseCardTag)<{ $tone: 'information' | 'warning' | 'success' | 'error' }>`
    margin-left: 0;
    color: ${(props) => props.theme.app.textColor};
    background: ${(props) => props.theme.app.component.notificationBanner[props.$tone].backgroundColor};
`;

const VariantMappingReplacementChoices: FC<{
    candidates: readonly ReplacementCandidate[];
    onChange: (key: string, replacement: AttributeReplacement | null) => void;
}> = ({ candidates, onChange }) => {
    const errorIDPrefix = useId();
    if (!candidates.length) return null;
    return (
        <Stack $gap='8px'>
            <strong>{T._('Update values to match your store', 'Admin')}</strong>
            <div>
                {T._(
                    'These values saved in Zakeke no longer match the values in your store. Select the updated store value for each one below, checking any suggested selections.',
                    'Admin'
                )}
            </div>
            {candidates.map(({ key, attribute, count, options: storeOptions, replacement, isSelected, conflict }, index) => {
                const errorID = `${errorIDPrefix}-${index}`;
                const statusID = `${errorID}-status`;
                const options = storeOptions.map((option) => ({
                    label: option.label,
                    options: option.values.map<ValueChoice>((value) => ({
                        value: replacementKey(option.optionID, value.valueID),
                        label: value.label,
                        targetOptionID: option.optionID,
                        targetValueID: value.valueID
                    }))
                }));
                const targetKey = replacement ? replacementKey(replacement.Id, replacement.Value.Id) : null;
                return (
                    <FormControl
                        key={key}
                        label={
                            <ChoiceLabel $horizontal $gap='8px'>
                                <span>{`${attribute.Label}: ${attribute.Value.Label}`}</span>
                                <ChoiceStatus id={statusID} $tone={conflict ? 'error' : !replacement ? 'warning' : isSelected ? 'success' : 'information'}>
                                    {conflict
                                        ? T._('Check selection', 'Admin')
                                        : !replacement
                                          ? T._('Choose a value', 'Admin')
                                          : isSelected
                                            ? T._('Selected by you', 'Admin')
                                            : T._('Suggested match — please review', 'Admin')}
                                </ChoiceStatus>
                            </ChoiceLabel>
                        }
                        caption={stringFormat(T._('Used by {0} affected variants in this channel.', 'Admin'), count.toString())}
                    >
                        <Stack $gap='4px'>
                            <AdvancedSelect<ValueChoice>
                                aria-label={stringFormat(T._('Replacement for {0}: {1}', 'Admin'), attribute.Label, attribute.Value.Label)}
                                options={options}
                                value={options.flatMap((option) => option.options).find((option) => option.value === targetKey) ?? null}
                                placeholder={T._('Choose a store value', 'Admin')}
                                isClearable={isSelected}
                                isError={!!conflict}
                                aria-invalid={!!conflict}
                                aria-describedby={conflict ? `${statusID} ${errorID}` : statusID}
                                menuPosition='fixed'
                                onChange={(choice: ValueChoice | null) =>
                                    onChange(
                                        key,
                                        choice
                                            ? {
                                                  optionID: attribute.Id,
                                                  valueID: attribute.Value.Id,
                                                  targetOptionID: choice.targetOptionID,
                                                  targetValueID: choice.targetValueID
                                              }
                                            : null
                                    )
                                }
                            />
                            {conflict && (
                                <ValidationMessageStyled id={errorID} role='alert'>
                                    {describeValueReplacementConflict(conflict)}
                                </ValidationMessageStyled>
                            )}
                        </Stack>
                    </FormControl>
                );
            })}
        </Stack>
    );
};

export default VariantMappingReplacementChoices;
